import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { ElMessage } from 'element-plus'
import store from '@/store'
import { $t, getLanguage } from '@/lang/index'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { debounce } from 'lodash-es'

dayjs.extend(utc)
dayjs.extend(timezone)

interface AxiosRequest extends AxiosRequestConfig {
  /** 不注入 Authorization header */
  notAuth?: boolean
  /** 不检查登录状态 */
  isNotAuth?: boolean
  /** 不弹出错误消息 */
  isNotMessage?: boolean
  /** 静默模式，不弹出任何提示 */
  silence?: boolean
  [key: string]: any
}

/** 不拼接 baseUrl 的 URL 前缀白名单 */
const URL_WHITELIST_PREFIXES = ['/File', '/Nebula.DMMS.Server.Hosting', '/Nebula.XinLiEIMS.Server.Hosting']

/** 登录请求的错误状态码处理映射，500+ 由 fallbackServerError 处理 */
const LOGIN_ERROR_STATUS_MAP: Record<number, string | undefined> = {
  400: undefined, // 使用 errFormat.message
  403: 'main_request_tip_no_legal',
  409: undefined // 使用 errFormat.message
}

const popErrorMessage = (message: string) => {
  ElMessage({ message, type: 'error', customClass: 'zIndex-6666' })
}

/** 通用的错误格式化与提示 */
function formatErrorResponse(
  error: any,
  options: {
    /** 解析 error.response.data 的方式：'full' 解析 string/object/file, 'simple' 只取 tips/display */
    parseMode?: 'full' | 'simple'
    /** 自定义状态码处理，key 为状态码，value 为国际化 key（undefined 则使用 errFormat.message） */
    statusMap?: Record<number, string | undefined>
    /** 500+ 状态码统一提示的国际化 key */
    fallbackServerError?: string
  } = {}
) {
  const { parseMode = 'full', statusMap, fallbackServerError } = options
  const errFormat = { message: '', tips: '', status: null as number | null }

  if (error.response) {
    const { status, data } = error.response
    if (parseMode === 'full') {
      if (typeof data === 'string') {
        errFormat.message = data
      } else if (Object.prototype.toString.call(data) === '[object Object]') {
        errFormat.message =
          (data as any).Display ||
          (data as any).display ||
          (data as any).Tips ||
          (data as any).tips ||
          $t('main_request_tip_system_error')
      } else {
        // fileToJson 需要异步处理，这里跳过，由调用方单独处理
      }
    } else {
      errFormat.message = (data as any).tips || (data as any).display || $t('main_request_tip_server_error')
    }
    errFormat.status = status

    // 优先匹配 statusMap 中的精确状态码
    if (statusMap && Object.prototype.hasOwnProperty.call(statusMap, status)) {
      const i18nKey = statusMap[status]
      if (i18nKey !== undefined) {
        popErrorMessage($t(i18nKey))
      } else {
        popErrorMessage(errFormat.message)
      }
    } else if (fallbackServerError && status >= 500) {
      popErrorMessage($t(fallbackServerError))
    }
  } else if (error.request) {
    popErrorMessage($t('main_request_tip_timeout'))
  }

  return errFormat
}

const state: any = store.state
const env = state.env.env
const request = axios.create({
  baseURL: '',
  timeout: 10000,
  headers: {
    zoneId: dayjs.tz.guess()
  }
})

function refreshToken() {
  return axios.create()({
    method: 'GET',
    url: `${
      process.env.NODE_ENV === 'development' ? '' : 'https://authentication.yunexpress.com'
    }/Authentication/GetAuthentication`,
    params: {
      key: state.user.userInfo.token
    }
  })
}

const debounceRefreshToken = debounce(refreshToken, 1000, {
  leading: true,
  trailing: false
})

request.interceptors.request.use(
  (config: AxiosRequest) => {
    if (!config.headers) {
      config.headers = {}
    }
    const { userInfo } = state.user
    if (userInfo) {
      if (!config.notAuth) {
        config.headers.Authorization = `Nebula token:${userInfo.token}`
        // 根据expired判断token是否过期
        if (userInfo.expired && userInfo.token) {
          const isExpired = dayjs().isAfter(dayjs(userInfo.expired))
          if (isExpired) {
            debounceRefreshToken()
              .then((res: any) => {
                const userInfo = {
                  name: res.data.info.meta.Name,
                  uid: res.data.info.uid,
                  token: res.data.token,
                  expired: res.data.expired
                }
                store.commit('user/setUserInfo', userInfo)
              })
              .catch(() => false)
          }
        }
      }
    }
    if (!config.isNotAuth && !JSON.parse(localStorage.getItem('userInfo') as string)) {
      store.commit('user/redirectLogin')
    }

    const language = getLanguage()
    if (language) {
      config.headers['Accept-Language'] = language
    }

    const baseUrl = state.baseUrl.baseUrl
    // const baseUrl = 'https://services-portal-uat.yunexpress.com'
    if (config.url?.startsWith('http')) {
      return config
    }

    // 本地开发（localhost）：白名单路径走 devServer proxy，不拼接 baseUrl
    if (window.location.hostname === 'localhost' && URL_WHITELIST_PREFIXES.some(prefix => config.url?.startsWith(prefix))) {
      return config
    }

    if (baseUrl) {
      config.url = `${baseUrl}${config.url}`
    } else if (env) {
      config.url = `http://${
        env.toLowerCase() === 'dev' ? 'gateway' : 'aligateway'
      }.${env.toLowerCase()}.yunexpress.com${config.url}`
    } else {
      store.commit('user/redirectLogin')
      return Promise.reject(new Error($t('main_request_tip_exist_environ')))
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

let isRefreshing = false // 控制 token 刷新状态
let pendingRequests: any[] = [] // 存储刷新 token 期间过来的 401 请求

const fileToJson = (res: any) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsText(res)
    reader.onload = (result: any) => {
      let msg = ''
      try {
        const resData = JSON.parse(result.target.result)
        if (resData) {
          msg = resData.display || resData.tips || resData.Display || resData.Tips
        }
      } catch (error) {
        console.log(error)
      }
      resolve(msg)
    }
  })
}

request.interceptors.response.use(
  (response: AxiosResponse) => {
    // handleResponseIntercept(response.config)
    return response
  },
  async error => {
    if (error.config.silence) return Promise.reject(error)
    // 超出 2xx 的状态码
    const errFormat = {
      message: '',
      tips: '',
      status: null
    }
    if (error.response) {
      // 请求发出收到响应，但是状态码超出 2xx 范围
      const { status, data } = error.response
      if (typeof data === 'string') {
        errFormat.message = data
      } else if (Object.prototype.toString.call(data) === '[object Object]') {
        errFormat.message =
          (data as any).Display ||
          (data as any).display ||
          (data as any).Tips ||
          (data as any).tips ||
          $t('main_request_tip_system_error')
      } else {
        const message: any = await fileToJson(data)
        errFormat.message = message
      }
      errFormat.status = status
      if (status === 401) {
        // token 无效 (没有提供 token、token 是无效的、token 过期)
        if (!state.user.userInfo) {
          ElMessage.warning($t('main_request_tip_login_expired'))
          // 没有登录
          store.commit('user/redirectLogin')
          return Promise.reject(error)
        }
        // 刷新 token
        if (!isRefreshing) {
          isRefreshing = true
          // 尝试刷新 token
          return refreshToken()
            .then((res: any) => {
              const userInfo = {
                name: res.data.info.meta.Name,
                uid: res.data.info.uid,
                token: res.data.token,
                expired: data.expired
              }
              store.commit('user/setUserInfo', userInfo)
              // 重新发送队列中的请求
              pendingRequests.forEach(cb => cb())
              // 重置 pendingRequests 数组
              pendingRequests = []
              return request(error.config)
            })
            .catch((error: any) => {
              ElMessage.warning($t('main_request_tip_login_expired'))
              store.commit('user/setUserInfo', null)
              store.commit('user/redirectLogin')
              return Promise.reject(error)
            })
            .finally(() => {
              isRefreshing = false // 重置刷新状态
            })
        }
        // 正在刷新状态下，把请求挂起放到 requests 数组中
        return new Promise(resolve => {
          pendingRequests.push(() => {
            resolve(request(error.config))
          })
        })
      } else if (status === 403) {
        popErrorMessage($t('main_request_tip_no_legal'))
      } else if (status.toString().startsWith('4')) {
        if (!error.config.isNotMessage) {
          errFormat.message
            ? popErrorMessage(errFormat.message)
            : popErrorMessage($t('main_request_tip_system_error'))
        }
      } else if (status === 504) {
        popErrorMessage($t('main_request_504_error'))
      } else if (status >= 500) {
        popErrorMessage($t('main_request_tip_server_error'))
      }
    } else if (error.request) {
      popErrorMessage($t('main_request_tip_timeout'))
    }
    return Promise.reject(errFormat)
  }
)

const loginRequest = axios.create({
  baseURL: process.env.NODE_ENV === 'development' ? '' : 'https://authentication.yunexpress.com',
  timeout: 10000
})

loginRequest.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async error => {
    const errFormat = formatErrorResponse(error, {
      parseMode: 'simple',
      statusMap: LOGIN_ERROR_STATUS_MAP,
      fallbackServerError: 'main_request_tip_server_error'
    })
    return Promise.reject(errFormat)
  }
)

export { loginRequest }
export default request
